import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import mammoth from 'mammoth/mammoth.browser';

export type PreviewKind = 'pdf' | 'image' | 'docx' | 'other';

/** Largest file the dialog will buffer; above this it offers Download only. */
export const MAX_PREVIEW_BYTES = 25 * 1024 * 1024;

const MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};

/** Lower-cased extension of a file name, ignoring any query string. */
export function fileExtension(filename?: string | null): string {
  return (filename?.split('?')[0].split('.').pop() || '').toLowerCase();
}

export function previewKind(filename?: string | null): PreviewKind {
  const ext = fileExtension(filename);
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'docx') return 'docx';
  return 'other';
}

/**
 * mammoth performs no sanitisation of the source document (its README says
 * so explicitly): a .docx hyperlink to javascript:... comes out as an <a>
 * that runs in the app origin when clicked. Uploaded records are untrusted
 * input, so the HTML is sanitised before it is rendered. Links may only be
 * http(s) or mailto; images keep mammoth's inline data: URIs.
 */
export function sanitizeDocxHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'svg', 'math', 'form', 'input', 'button'],
    ALLOWED_URI_REGEXP:
      /^(?:https?|mailto|data:image\/(?:png|jpe?g|gif|webp))/i,
    ADD_ATTR: ['target'],
  });
}

type PreviewFailure =
  | { kind: 'network' }
  | { kind: 'http'; status: number }
  | { kind: 'too-large' }
  | { kind: 'other' };

class TooLargeError extends Error {}

/**
 * Reads a response body up to maxBytes; throws TooLargeError past that so a
 * multi-hundred-megabyte record never lands in the JS heap.
 */
async function readBodyCapped(
  res: Response,
  maxBytes: number
): Promise<ArrayBuffer> {
  const declared = Number(res.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > maxBytes) {
    await res.body?.cancel().catch(() => undefined);
    throw new TooLargeError();
  }
  if (!res.body) {
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength > maxBytes) throw new TooLargeError();
    return buffer;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      received += value.byteLength;
      if (received > maxBytes) {
        await reader.cancel().catch(() => undefined);
        throw new TooLargeError();
      }
      chunks.push(value);
    }
  }
  const out = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out.buffer;
}

interface DocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  url?: string;
  filename: string;
  onDownload?: () => void;
  /** Stored size when the caller knows it; over the cap skips the fetch. */
  sizeBytes?: number;
  /** Test hook; production uses MAX_PREVIEW_BYTES. */
  maxBytes?: number;
}

/**
 * Shows an uploaded record inside the app (ZLAYT-1417, QA D-21).
 *
 * Preview used to window.open() the storage URL in a new tab, which in
 * production showed a browser warning and landed on the marketing site. The
 * file is fetched (no credentials: the URL is presigned) and shown from a
 * blob typed by its extension, so it renders even when the stored object's
 * Content-Type is generic. The blob URL is same-origin with the app; that is
 * safe for pdf and image because the type is pinned by us, and for docx
 * because the HTML is sanitised. Anything else is offered as a download.
 */
function DocumentPreviewDialog({
  open,
  onClose,
  url,
  filename,
  onDownload,
  sizeBytes,
  maxBytes = MAX_PREVIEW_BYTES,
}: DocumentPreviewDialogProps) {
  const kind = previewKind(filename);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<PreviewFailure | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const releaseObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!open || !url || kind === 'other') return undefined;
    let cancelled = false;
    setLoading(true);
    setFailure(null);
    setObjectUrl(null);
    setDocxHtml(null);
    releaseObjectUrl();

    if (Number.isFinite(sizeBytes) && (sizeBytes as number) > maxBytes) {
      setFailure({ kind: 'too-large' });
      setLoading(false);
      return undefined;
    }

    (async () => {
      try {
        let res: Response;
        try {
          res = await fetch(url, { credentials: 'omit' });
        } catch (e) {
          // fetch rejects (TypeError) on network failure and on a missing
          // CORS rule; an HTTP error status resolves normally.
          throw Object.assign(new Error('network'), { previewKind: 'network' });
        }
        if (!res.ok) {
          throw Object.assign(new Error(`HTTP ${res.status}`), {
            previewKind: 'http',
            status: res.status,
          });
        }
        const buffer = await readBodyCapped(res, maxBytes);
        if (cancelled) return;
        if (kind === 'docx') {
          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          if (!cancelled) setDocxHtml(sanitizeDocxHtml(result.value || ''));
        } else {
          const ext = fileExtension(filename);
          const createdUrl = URL.createObjectURL(
            new Blob([buffer], {
              type: MIME[ext] || 'application/octet-stream',
            })
          );
          objectUrlRef.current = createdUrl;
          setObjectUrl(createdUrl);
        }
      } catch (e: any) {
        console.error('[DocumentPreviewDialog] preview failed', e);
        if (cancelled) return;
        if (e instanceof TooLargeError) setFailure({ kind: 'too-large' });
        else if (e?.previewKind === 'network') setFailure({ kind: 'network' });
        else if (e?.previewKind === 'http')
          setFailure({ kind: 'http', status: e.status });
        else setFailure({ kind: 'other' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, url, kind, filename, sizeBytes, maxBytes]);

  // Revoke whatever is still held when the owner unmounts.
  useEffect(() => releaseObjectUrl, []);

  const failureText = (f: PreviewFailure): string => {
    switch (f.kind) {
      case 'too-large':
        return 'This file is too large to preview in the app.';
      case 'network':
        return 'This file could not be fetched. Check your connection; if it persists, the storage access rule may be blocking the app.';
      case 'http':
        return `This file could not be previewed: storage refused the request (HTTP ${f.status}).`;
      default:
        return 'This file could not be previewed.';
    }
  };

  const renderBody = () => {
    if (kind === 'other' || failure || !url) {
      return (
        <Box py={6} textAlign="center" role="status">
          <Typography variant="body1">
            {kind === 'other' || !url
              ? 'This file type cannot be previewed in the app.'
              : failureText(failure as PreviewFailure)}
          </Typography>
          {onDownload && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              You can download it instead.
            </Typography>
          )}
        </Box>
      );
    }
    if (loading) {
      return (
        <Box py={6} display="flex" justifyContent="center">
          <CircularProgress size={28} aria-label="loading preview" />
        </Box>
      );
    }
    if (kind === 'docx' && docxHtml !== null) {
      return (
        <Box
          px={2}
          sx={{ '& img': { maxWidth: '100%' } }}
          // Sanitised by sanitizeDocxHtml before it gets here.
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      );
    }
    if (kind === 'image' && objectUrl) {
      return (
        <Box display="flex" justifyContent="center">
          <img
            src={objectUrl}
            alt={`Preview of ${filename}`}
            style={{ maxWidth: '100%' }}
          />
        </Box>
      );
    }
    if (kind === 'pdf' && objectUrl) {
      return (
        <iframe
          title={`Preview of ${filename}`}
          src={objectUrl}
          style={{ width: '100%', height: '75vh', border: 'none' }}
        />
      );
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="document-preview-title"
    >
      <DialogTitle
        id="document-preview-title"
        sx={{ pr: 6, wordBreak: 'break-all' }}
      >
        {filename}
        <IconButton
          aria-label="close preview"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      {onDownload && (
        <DialogActions>
          <Button onClick={onDownload}>Download</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default DocumentPreviewDialog;
